import { useState, useMemo, useCallback } from "react";
import { useLocation, useSearch, Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAnalyticsKpis,
  useGetCommitmentSeries,
  useGetBuyoutSeries,
  useGetSpendByDimension,
  useGetExpiringSoon,
  useListAnalyticsPresets,
  useCreateAnalyticsPreset,
  useDeleteAnalyticsPreset,
  getListAnalyticsPresetsQueryKey,
} from "@workspace/api-client-react";
import type {
  GetAnalyticsKpisParams,
  GetCommitmentSeriesParams,
  GetBuyoutSeriesParams,
  GetSpendByDimensionParams,
  GetExpiringSoonParams,
  AnalyticsPreset,
  AnalyticsFilters,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import {
  DollarSign, Users, TrendingUp, TrendingDown, AlertCircle,
  Download, BarChart3, Clock, ArrowUpDown, ArrowUp, ArrowDown,
  Calendar, Filter, Bookmark, BookmarkPlus, Trash2, ChevronDown,
  FileSpreadsheet, FileText,
} from "lucide-react";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number | null | undefined, opts?: { compact?: boolean; integer?: boolean }): string {
  if (n == null) return "—";
  if (opts?.compact) {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
  }
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtDelta(delta: number | null | undefined): { text: string; positive: boolean; Icon: React.ElementType } | null {
  if (delta == null) return null;
  const positive = delta >= 0;
  const text = `${positive ? "+" : ""}${fmt(delta, { compact: true })} vs last year`;
  return { text, positive, Icon: positive ? TrendingUp : TrendingDown };
}

function fmtCountDelta(delta: number | null | undefined): { text: string; positive: boolean } | null {
  if (delta == null) return null;
  const positive = delta >= 0;
  return { text: `${positive ? "+" : ""}${delta} vs last year`, positive };
}

const SPORT_COLORS = [
  "#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed",
  "#0891b2", "#be185d", "#65a30d", "#ea580c", "#6366f1",
  "#14b8a6", "#f59e0b",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

// ---------------------------------------------------------------------------
// Shared Filter Bar
// ---------------------------------------------------------------------------

export interface Filters {
  fiscalYearStart?: number;
  fiscalYearEnd?: number;
  sport?: string;
  role?: string;
  status?: "active" | "expired" | "all";
  groupBy?: "sport" | "role" | "conference";
}

export function FilterBar({
  filters,
  onChange,
  sports,
  roles,
  presetsSlot,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  sports: string[];
  roles: string[];
  presetsSlot?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap gap-3 items-center p-4 bg-muted/40 rounded-xl border">
      <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />

      <Select
        value={String(filters.fiscalYearStart ?? currentYear)}
        onValueChange={(v) => onChange({ ...filters, fiscalYearStart: parseInt(v) })}
      >
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="Year from" />
        </SelectTrigger>
        <SelectContent>
          {YEARS.map((y) => (
            <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-xs text-muted-foreground">to</span>

      <Select
        value={String(filters.fiscalYearEnd ?? currentYear + 4)}
        onValueChange={(v) => onChange({ ...filters, fiscalYearEnd: parseInt(v) })}
      >
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="Year to" />
        </SelectTrigger>
        <SelectContent>
          {YEARS.map((y) => (
            <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.sport ?? "all_sports"}
        onValueChange={(v) => onChange({ ...filters, sport: v === "all_sports" ? undefined : v })}
      >
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="All sports" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all_sports">All Sports</SelectItem>
          {sports.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.role ?? "all_roles"}
        onValueChange={(v) => onChange({ ...filters, role: v === "all_roles" ? undefined : v })}
      >
        <SelectTrigger className="w-40 h-8 text-xs">
          <SelectValue placeholder="All roles" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all_roles">All Roles</SelectItem>
          {roles.map((r) => (
            <SelectItem key={r} value={r}>{r}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? "active"}
        onValueChange={(v) => onChange({ ...filters, status: v as Filters["status"] })}
      >
        <SelectTrigger className="w-32 h-8 text-xs">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="expired">Expired</SelectItem>
          <SelectItem value="all">All</SelectItem>
        </SelectContent>
      </Select>

      {(filters.sport || filters.role || filters.status !== "active") && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={() => onChange({ fiscalYearStart: currentYear, fiscalYearEnd: currentYear + 4, status: "active" })}
        >
          Clear filters
        </Button>
      )}

      {presetsSlot && <div className="ml-auto flex items-center gap-2">{presetsSlot}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved-view (preset) controls
// ---------------------------------------------------------------------------

export function SavedViewsControls({
  filters,
  onLoad,
}: {
  filters: Filters;
  onLoad: (f: Filters) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: presets, isLoading } = useListAnalyticsPresets();
  const createMutation = useCreateAnalyticsPreset();
  const deleteMutation = useDeleteAnalyticsPreset();

  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListAnalyticsPresetsQueryKey() });

  function handleSave() {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      toast({ title: "Name required", description: "Give this view a name so you can find it later.", variant: "destructive" });
      return;
    }
    const presetFilters: AnalyticsFilters = {
      fiscalYearStart: filters.fiscalYearStart,
      fiscalYearEnd: filters.fiscalYearEnd,
      sport: filters.sport,
      role: filters.role,
      status: filters.status,
    };
    createMutation.mutate(
      { data: { name: trimmed, filters: presetFilters } },
      {
        onSuccess: () => {
          toast({ title: "View saved", description: `"${trimmed}" is now in your saved views.` });
          setName("");
          setSaveOpen(false);
          invalidate();
        },
        onError: () => {
          toast({ title: "Couldn't save view", description: "Please try again.", variant: "destructive" });
        },
      },
    );
  }

  function handleLoad(preset: AnalyticsPreset) {
    const f = preset.filters ?? {};
    onLoad({
      fiscalYearStart: f.fiscalYearStart ?? currentYear,
      fiscalYearEnd: f.fiscalYearEnd ?? currentYear + 4,
      sport: f.sport,
      role: f.role,
      status: (f.status as Filters["status"]) ?? "active",
    });
    toast({ title: "View loaded", description: `Showing "${preset.name}".` });
  }

  function handleDelete(preset: AnalyticsPreset) {
    if (!window.confirm(`Delete the saved view "${preset.name}"?`)) return;
    deleteMutation.mutate(
      { id: preset.id },
      {
        onSuccess: () => {
          toast({ title: "View deleted", description: `"${preset.name}" was removed.` });
          invalidate();
        },
        onError: () => {
          toast({ title: "Couldn't delete view", description: "Please try again.", variant: "destructive" });
        },
      },
    );
  }

  const presetCount = presets?.length ?? 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="button-saved-views">
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />
            Saved views
            {presetCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                {presetCount}
              </Badge>
            )}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="text-xs">Saved analytics views</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isLoading ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">Loading…</div>
          ) : presetCount === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              No saved views yet. Configure your filters and click Save view to bookmark them.
            </div>
          ) : (
            presets!.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-accent rounded-sm"
                data-testid={`row-preset-${p.id}`}
              >
                <button
                  type="button"
                  className="flex-1 min-w-0 text-left"
                  onClick={() => handleLoad(p)}
                  data-testid={`button-load-preset-${p.id}`}
                >
                  <div className="text-xs font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {summarizePresetFilters(p.filters)}
                    {!p.isOwn && <span className="ml-1">· shared</span>}
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(p);
                  }}
                  data-testid={`button-delete-preset-${p.id}`}
                  aria-label={`Delete ${p.name}`}
                >
                  <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setSaveOpen(true);
            }}
            data-testid="button-open-save-view"
          >
            <BookmarkPlus className="h-3.5 w-3.5 mr-2" />
            <span className="text-xs">Save current view…</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="default"
        size="sm"
        className="h-8 text-xs"
        onClick={() => setSaveOpen(true)}
        data-testid="button-save-view"
      >
        <BookmarkPlus className="h-3.5 w-3.5 mr-1.5" />
        Save view
      </Button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save analytics view</DialogTitle>
            <DialogDescription>
              Bookmark the current filter combination so you can return to it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="preset-name" className="text-xs">View name</Label>
              <Input
                id="preset-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Football contracts expiring this year"
                maxLength={100}
                autoFocus
                data-testid="input-preset-name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            </div>
            <div className="rounded-md border bg-muted/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Filters being saved
              </div>
              <div className="text-xs text-foreground">
                {summarizeFilters(filters)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMutation.isPending} data-testid="button-confirm-save-view">
              {createMutation.isPending ? "Saving…" : "Save view"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function summarizeFilters(f: Filters): string {
  const parts: string[] = [];
  const yStart = f.fiscalYearStart ?? currentYear;
  const yEnd = f.fiscalYearEnd ?? currentYear + 4;
  parts.push(`FY ${yStart}–${yEnd}`);
  if (f.sport) parts.push(f.sport);
  if (f.role) parts.push(f.role);
  parts.push(`${f.status ?? "active"} contracts`);
  return parts.join(" · ");
}

function summarizePresetFilters(f: AnalyticsFilters | undefined): string {
  if (!f) return "All filters";
  return summarizeFilters({
    fiscalYearStart: f.fiscalYearStart,
    fiscalYearEnd: f.fiscalYearEnd,
    sport: f.sport,
    role: f.role,
    status: f.status as Filters["status"],
  });
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

function KpiCard({
  title,
  value,
  delta,
  icon: Icon,
  color = "blue",
  isLoading,
}: {
  title: string;
  value: string;
  delta?: ReturnType<typeof fmtDelta> | ReturnType<typeof fmtCountDelta> | null;
  icon: React.ElementType;
  color?: "blue" | "green" | "gold" | "red";
  isLoading?: boolean;
}) {
  const colorMap = {
    blue: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", icon: "text-blue-500" },
    green: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", icon: "text-emerald-500" },
    gold: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", icon: "text-amber-500" },
    red: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", icon: "text-red-500" },
  };
  const c = colorMap[color];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight">{title}</CardTitle>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
          <Icon className={`h-4 w-4 ${c.icon}`} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <Skeleton className="h-8 w-32 mb-1" />
        ) : (
          <div className={`text-2xl font-bold kpi-value tracking-tight ${c.text}`}>{value}</div>
        )}
        {delta && !isLoading && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${delta.positive ? "text-emerald-600" : "text-red-500"}`}>
            {"Icon" in delta ? <delta.Icon className="h-3 w-3" /> : null}
            <span>{delta.text}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Commitment Chart
// ---------------------------------------------------------------------------

function CommitmentChart({ filters }: { filters: Filters }) {
  const [groupBy, setGroupBy] = useState<"sport" | "role" | "conference">("sport");

  const params: GetCommitmentSeriesParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetCommitmentSeriesParams["status"],
    groupBy,
  };

  const { data, isLoading } = useGetCommitmentSeries(params);

  const { chartData, dimensions } = useMemo(() => {
    if (!data || data.length === 0) return { chartData: [], dimensions: [] };

    const yearStart = filters.fiscalYearStart ?? currentYear;
    const yearEnd = filters.fiscalYearEnd ?? currentYear + 4;

    const dimSet = new Set(data.map((d) => d.dimension));
    const dims = Array.from(dimSet).filter((d) => d !== "No Data");

    const byYear: Record<number, Record<string, number>> = {};
    for (let yr = yearStart; yr <= yearEnd; yr++) {
      byYear[yr] = {};
    }
    for (const item of data) {
      if (item.dimension === "No Data") continue;
      if (!byYear[item.fiscalYear]) byYear[item.fiscalYear] = {};
      byYear[item.fiscalYear][item.dimension] = (byYear[item.fiscalYear][item.dimension] ?? 0) + item.guaranteedComp;
    }

    const chartData = Object.entries(byYear)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([yr, dimValues]) => ({ year: `FY ${yr}`, ...dimValues }));

    return { chartData, dimensions: dims };
  }, [data, filters]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-semibold">Multi-Year Compensation Commitment</CardTitle>
            <CardDescription className="text-xs mt-0.5">Guaranteed comp by fiscal year (base + recurring bonuses + allowances)</CardDescription>
          </div>
          <div className="flex gap-1">
            {(["sport", "role", "conference"] as const).map((g) => (
              <Button
                key={g}
                variant={groupBy === g ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs capitalize"
                onClick={() => setGroupBy(g)}
              >
                By {g}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
            No commitment data available for the selected filters.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`}
                tick={{ fontSize: 11 }}
                width={60}
              />
              <Tooltip
                formatter={(value: number) => [fmt(value, { compact: true }), ""]}
                contentStyle={{ fontSize: 11 }}
              />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              {dimensions.map((dim, i) => (
                <Bar
                  key={dim}
                  dataKey={dim}
                  stackId="comp"
                  fill={SPORT_COLORS[i % SPORT_COLORS.length]}
                  name={dim}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Buyout Series Chart + Top-10 Table
// ---------------------------------------------------------------------------

function BuyoutChart({ filters }: { filters: Filters }) {
  const params: GetBuyoutSeriesParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetBuyoutSeriesParams["status"],
  };

  const { data, isLoading } = useGetBuyoutSeries(params);

  const chartData = useMemo(() => {
    if (!data?.series) return [];
    return data.series
      .filter((_, i) => i % 3 === 0)
      .map((s) => ({
        month: new Date(s.month).toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
        exposure: s.totalBuyoutExposure,
      }));
  }, [data]);

  const topBuyouts = data?.topBuyouts ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Buyout Exposure Over Time</CardTitle>
          <CardDescription className="text-xs mt-0.5">If-terminated-today exposure projected over 60 months as contracts age</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {isLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">
              No buyout exposure data for the selected filters.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={3} />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`}
                  tick={{ fontSize: 10 }}
                  width={60}
                />
                <Tooltip
                  formatter={(v: number) => [fmt(v, { compact: true }), "Buyout Exposure"]}
                  contentStyle={{ fontSize: 11 }}
                />
                <Line
                  type="monotone"
                  dataKey="exposure"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  name="Buyout Exposure"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Top Buyout Exposures</CardTitle>
          <CardDescription className="text-xs mt-0.5">Individual contracts with highest buyout today</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : topBuyouts.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-xs">No buyout data available.</div>
          ) : (
            <div className="divide-y">
              {topBuyouts.slice(0, 10).map((entry, i) => (
                <div key={entry.coachId + entry.contractEndDate} className="flex items-center justify-between px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{entry.coachName}</div>
                    <div className="text-[10px] text-muted-foreground">{entry.sport} · ends {entry.contractEndDate}</div>
                  </div>
                  <div className="text-xs font-bold text-red-600 dark:text-red-400 ml-2 flex-shrink-0">
                    {fmt(entry.buyoutExposure, { compact: true })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spend by Dimension Table
// ---------------------------------------------------------------------------

type SortKey = "dimension" | "activeCoachCount" | "avgBaseSalary" | "totalBonuses" | "totalAllowances" | "totalGuaranteedComp" | "avgBuyoutExposure";

function SpendTable({
  filters,
  groupBy,
  onGroupByChange,
}: {
  filters: Filters;
  groupBy: "sport" | "role" | "conference";
  onGroupByChange: (g: "sport" | "role" | "conference") => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("totalGuaranteedComp");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const params: GetSpendByDimensionParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetSpendByDimensionParams["status"],
    groupBy,
  };

  const { data, isLoading } = useGetSpendByDimension(params);

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const aVal = a[sortKey as keyof typeof a] ?? 0;
      const bVal = b[sortKey as keyof typeof b] ?? 0;
      if (typeof aVal === "string") return sortDir === "asc" ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
      return sortDir === "asc" ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [data, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function SortHeader({ k, label }: { k: SortKey; label: string }) {
    const active = sortKey === k;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead
        className="text-xs cursor-pointer select-none hover:text-foreground whitespace-nowrap"
        onClick={() => handleSort(k)}
      >
        <div className="flex items-center gap-1">
          {label}
          <Icon className={`h-3 w-3 ${active ? "text-primary" : "text-muted-foreground/50"}`} />
        </div>
      </TableHead>
    );
  }

  const dimLabel = groupBy === "sport" ? "Sport" : groupBy === "role" ? "Role / Title" : "Conference";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-semibold">Spend by {dimLabel}</CardTitle>
            <CardDescription className="text-xs mt-0.5">Sortable breakdown of compensation across dimensions</CardDescription>
          </div>
          <div className="flex gap-1">
            {(["sport", "role", "conference"] as const).map((g) => (
              <Button
                key={g}
                variant={groupBy === g ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs capitalize"
                onClick={() => onGroupByChange(g)}
              >
                By {g}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No data available for the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader k="dimension" label={dimLabel} />
                  <SortHeader k="activeCoachCount" label="Coaches" />
                  <SortHeader k="avgBaseSalary" label="Avg Base Salary" />
                  <SortHeader k="totalBonuses" label="Total Bonuses" />
                  <SortHeader k="totalAllowances" label="Total Allowances" />
                  <SortHeader k="totalGuaranteedComp" label="Total Guaranteed Comp" />
                  <SortHeader k="avgBuyoutExposure" label="Avg Buyout Exposure" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.dimension}>
                    <TableCell className="font-medium text-sm">{row.dimension}</TableCell>
                    <TableCell className="text-sm">{row.activeCoachCount}</TableCell>
                    <TableCell className="text-sm">{fmt(row.avgBaseSalary, { compact: true })}</TableCell>
                    <TableCell className="text-sm">{fmt(row.totalBonuses, { compact: true })}</TableCell>
                    <TableCell className="text-sm">{fmt(row.totalAllowances, { compact: true })}</TableCell>
                    <TableCell className="text-sm font-semibold">{fmt(row.totalGuaranteedComp, { compact: true })}</TableCell>
                    <TableCell className="text-sm text-red-600 dark:text-red-400">{fmt(row.avgBuyoutExposure, { compact: true })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Expiring Soon Panel
// ---------------------------------------------------------------------------

function ExpiringSoonPanel({ filters }: { filters: Filters }) {
  const params: GetExpiringSoonParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetExpiringSoonParams["status"],
  };

  const { data, isLoading } = useGetExpiringSoon(params);

  const buckets = [
    { label: "Next 90 Days", key: "in90Days" as const, color: "text-red-600 dark:text-red-400", bg: "bg-red-500/8" },
    { label: "Next 180 Days", key: "in180Days" as const, color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-500/8" },
    { label: "Next 365 Days", key: "in365Days" as const, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/8" },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">Expiring Soon</CardTitle>
        </div>
        <CardDescription className="text-xs">Dollar value of contracts expiring within time windows</CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {buckets.map((b) => {
              const bucket = data?.[b.key];
              return (
                <div key={b.key} className={`rounded-xl border p-4 ${b.bg}`}>
                  <div className="text-xs text-muted-foreground mb-1 font-medium">{b.label}</div>
                  <div className={`text-xl font-bold kpi-value ${b.color}`}>
                    {bucket ? fmt(bucket.totalValue, { compact: true }) : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {bucket?.count ?? 0} contract{bucket?.count !== 1 ? "s" : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function AnalyticsEmptyState() {
  return (
    <Card className="border-dashed border-2">
      <CardContent className="py-16 px-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <BarChart3 className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-lg font-bold mb-2">No data yet</h2>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Add coaches and contracts to your organization and analytics will appear here automatically.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Access Guard
// ---------------------------------------------------------------------------

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <h2 className="text-lg font-semibold">Access Restricted</h2>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        The Analytics dashboard is available to Admin and Finance roles only.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// URL search params helpers
// ---------------------------------------------------------------------------

export function filtersToSearchParams(f: Filters): string {
  const p = new URLSearchParams();
  if (f.fiscalYearStart != null) p.set("fyStart", String(f.fiscalYearStart));
  if (f.fiscalYearEnd != null) p.set("fyEnd", String(f.fiscalYearEnd));
  if (f.sport) p.set("sport", f.sport);
  if (f.role) p.set("role", f.role);
  if (f.status && f.status !== "active") p.set("status", f.status);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function searchParamsToFilters(search: string): Filters {
  const p = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return {
    fiscalYearStart: p.has("fyStart") ? parseInt(p.get("fyStart")!) : currentYear,
    fiscalYearEnd: p.has("fyEnd") ? parseInt(p.get("fyEnd")!) : currentYear + 4,
    sport: p.get("sport") ?? undefined,
    role: p.get("role") ?? undefined,
    status: (p.get("status") as Filters["status"]) ?? "active",
  };
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function Analytics() {
  const { org } = useOrg();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [spendGroupBy, setSpendGroupBy] = useState<"sport" | "role" | "conference">("sport");
  const search = useSearch();
  const [, setLocation] = useLocation();

  const filters: Filters = useMemo(() => searchParamsToFilters(search), [search]);

  const setFilters = useCallback((f: Filters) => {
    setLocation(`/analytics${filtersToSearchParams(f)}`, { replace: true });
  }, [setLocation]);

  // Redirect deep-links that targeted the old Equity tab to the new /equity page,
  // preserving filter context. Done before the Finance access gate so non-Finance
  // members who follow an old equity deep-link still land on /equity (which is
  // open to all signed-in members) instead of seeing AccessDenied.
  const equityRedirect = useMemo(() => {
    const p = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const hash = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
    if (p.get("tab") !== "equity" && hash !== "equity") return null;
    p.delete("tab");
    const qs = p.toString();
    return `/equity${qs ? `?${qs}` : ""}`;
  }, [search]);
  if (equityRedirect) return <Redirect to={equityRedirect} replace />;

  // Role gate: Admin or Finance persona only
  const isAdmin = org?.role === "admin";
  const isFinance = org?.personas?.includes("Finance") ?? false;
  if (org && !isAdmin && !isFinance) {
    return <AccessDenied />;
  }

  const kpiParams: GetAnalyticsKpisParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetAnalyticsKpisParams["status"],
  };

  const { data: kpis, isLoading: kpisLoading } = useGetAnalyticsKpis(kpiParams);

  // Derive available sports and roles from spend data
  const spendParams: GetSpendByDimensionParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetSpendByDimensionParams["status"],
    groupBy: "sport",
  };
  const { data: spendBySport } = useGetSpendByDimension(spendParams);
  const { data: spendByRole } = useGetSpendByDimension({ ...spendParams, groupBy: "role" });

  const sports = useMemo(() => (spendBySport ?? []).map((r) => r.dimension), [spendBySport]);
  const roles = useMemo(() => (spendByRole ?? []).map((r) => r.dimension), [spendByRole]);

  const isEmpty = !kpisLoading && kpis?.activeCoachCount === 0;

  function buildExportParams() {
    const params = new URLSearchParams();
    if (filters.fiscalYearStart != null) params.set("fiscalYearStart", String(filters.fiscalYearStart));
    if (filters.fiscalYearEnd != null) params.set("fiscalYearEnd", String(filters.fiscalYearEnd));
    if (filters.sport) params.set("sport", filters.sport);
    if (filters.role) params.set("role", filters.role);
    if (filters.status) params.set("status", filters.status);
    return params;
  }

  async function downloadExport(url: string, filename: string) {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  async function handleExport(format: "csv" | "xlsx" = "csv") {
    setIsExporting(true);
    try {
      const params = buildExportParams();
      params.set("groupBy", spendGroupBy);
      if (format === "xlsx") params.set("format", "xlsx");
      await downloadExport(`/api/export/analytics.csv?${params}`, `analytics-${new Date().toISOString().split("T")[0]}.${format}`);
    } catch {
      toast({ title: "Export failed", description: "Could not export analytics data. Please try again.", variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  }

  async function handleExportPdf() {
    setIsPdfExporting(true);
    try {
      const params = buildExportParams();
      params.set("groupBy", spendGroupBy);
      await downloadExport(`/api/export/analytics.pdf?${params}`, `analytics-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch {
      toast({ title: "PDF export failed", description: "Could not generate the analytics PDF. Please try again.", variant: "destructive" });
    } finally {
      setIsPdfExporting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Analytics</h1>
          <p>Org-wide compensation and buyout exposure overview</p>
        </div>
        <div className="flex gap-2">
          <DemoExportGate label="Export" size="sm" variant="outline">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting || isEmpty}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  {isExporting ? "Exporting…" : "Export"}
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("csv")}>
                  <FileText className="h-3.5 w-3.5 mr-2" />
                  CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </DemoExportGate>
          <DemoExportGate label="Export PDF" size="sm" variant="outline">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              disabled={isPdfExporting || isEmpty}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {isPdfExporting ? "Generating…" : "Export PDF"}
            </Button>
          </DemoExportGate>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        sports={sports}
        roles={roles}
        presetsSlot={<SavedViewsControls filters={filters} onLoad={setFilters} />}
      />

      {isEmpty ? (
        <AnalyticsEmptyState />
      ) : (
        <>
          {/* KPI Cards */}
          <div>
            <p className="section-label">Headline KPIs</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                title="Total Annual Base Salary"
                value={fmt(kpis?.totalAnnualBaseSalary, { compact: true })}
                delta={fmtDelta(kpis?.totalAnnualBaseSalaryDelta)}
                icon={DollarSign}
                color="blue"
                isLoading={kpisLoading}
              />
              <KpiCard
                title="Total Guaranteed Comp"
                value={fmt(kpis?.totalGuaranteedComp, { compact: true })}
                delta={fmtDelta(kpis?.totalGuaranteedCompDelta)}
                icon={TrendingUp}
                color="green"
                isLoading={kpisLoading}
              />
              <KpiCard
                title="Total Buyout Exposure Today"
                value={fmt(kpis?.totalBuyoutExposure, { compact: true })}
                delta={fmtDelta(kpis?.totalBuyoutExposureDelta)}
                icon={AlertCircle}
                color="red"
                isLoading={kpisLoading}
              />
              <KpiCard
                title="Active Coach Count"
                value={String(kpis?.activeCoachCount ?? "—")}
                delta={fmtCountDelta(kpis?.activeCoachCountDelta)}
                icon={Users}
                color="gold"
                isLoading={kpisLoading}
              />
            </div>
          </div>

          {/* Commitment Chart */}
          <div>
            <p className="section-label">Multi-Year Commitment</p>
            <CommitmentChart filters={filters} />
          </div>

          {/* Buyout Exposure */}
          <div>
            <p className="section-label">Buyout Exposure</p>
            <BuyoutChart filters={filters} />
          </div>

          {/* Expiring Soon */}
          <div>
            <p className="section-label">Expiring Soon</p>
            <ExpiringSoonPanel filters={filters} />
          </div>

          {/* Spend Table */}
          <div>
            <p className="section-label">Spend Breakdown</p>
            <SpendTable filters={filters} groupBy={spendGroupBy} onGroupByChange={setSpendGroupBy} />
          </div>
        </>
      )}
    </div>
  );
}
